import React, { useState, useEffect, useCallback, useRef } from 'react';
import { format, isBefore, isAfter, subDays, subMonths } from 'date-fns';
import {
    Search, Calendar, Plus, Eye, Pencil, Trash,
    Download, RefreshCw, X, Check, Filter, ChevronDown,
    Clock, MapPin, QrCode, FileSpreadsheet, ClipboardCheck
} from 'lucide-react';
import { GET, POST, PUT, DELETE } from '../services/api';
import { useMasterChapter, MasterChapterLockNote } from '../context/MasterChapter';

const isImageFile = (file) => {
    if (!file) return false;
    return file.type?.startsWith('image/');
};

// --- Global Styles ---
const CustomStyles = () => (
    <style>{`
    .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
    .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 8px; }
    .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 8px; }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover { background-color: #94a3b8; }
    .animate-fade-in-up { animation: fade-in-up 0.4s ease-out forwards; }
    @keyframes fade-in-up { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
  `}</style>
);

// --- Toast Component ---
const Toast = ({ message, type, onClose }) => {
    if (!message) return null;
    const bgColor = type === 'success' ? 'bg-emerald-600' : 'bg-red-500';
    return (
        <div className={`fixed bottom-6 right-6 px-6 py-3 rounded-xl shadow-2xl text-white font-bold flex items-center gap-3 z-[1000] animate-in slide-in-from-bottom-5 ${bgColor}`}>
            {type === 'success' ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
            {message}
            <button onClick={onClose} className="ml-auto p-1 rounded-full hover:bg-white/20 transition-colors"><X className="w-4 h-4" /></button>
        </div>
    );
};

// --- Confirmation Modal ---
const ConfirmationModal = ({ title, message, onConfirm, onCancel, isOpen }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[999] p-4 animate-in fade-in">
            <div className="bg-white p-6 rounded-3xl shadow-2xl max-w-sm w-full text-center border border-slate-100 scale-100 animate-in zoom-in-95">
                <div className="w-14 h-14 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-red-50">
                    <Trash className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-2">{title}</h3>
                <p className="text-slate-500 mb-6 font-medium">{message}</p>
                <div className="flex gap-3">
                    <button onClick={onCancel} className="flex-1 px-4 py-2.5 border border-slate-300 rounded-xl hover:bg-slate-50 text-slate-700 font-bold transition-colors">Cancel</button>
                    <button onClick={onConfirm} className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 font-bold transition-colors shadow-lg shadow-red-200">Confirm</button>
                </div>
            </div>
        </div>
    );
};

// --- Manual Attendance Modal ---
// Lists the meeting chapter's members; only rows the admin changes are sent on save.
// QR-scanned "present" rows are locked (the backend enforces this too).
const AttendanceModal = ({ meeting, onClose, onSaved, showToast }) => {
    const [data, setData] = useState(null);
    const [draft, setDraft] = useState({});
    const [search, setSearch] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        GET(`/meetings/${meeting._id}/attendance`)
            .then(res => setData(res?.data || null))
            .catch(err => { showToast(err.message || 'Failed to load attendance.', 'error'); onClose(); });
    }, [meeting._id]); // eslint-disable-line react-hooks/exhaustive-deps

    const members = data?.members || [];
    const canEdit = Boolean(data?.canEdit);
    const statusOf = (m) => draft[m.user_id] ?? m.status;
    const changed = members.filter(m => draft[m.user_id] && draft[m.user_id] !== m.status);
    const visible = members.filter(m => `${m.name} ${m.phone}`.toLowerCase().includes(search.trim().toLowerCase()));
    const presentCount = members.filter(m => statusOf(m) === 'attended').length;

    const setStatus = (m, status) => { if (canEdit && !m.locked) setDraft(d => ({ ...d, [m.user_id]: status })); };
    const markAll = (status) => setDraft(d => {
        const next = { ...d };
        visible.forEach(m => { if (!m.locked) next[m.user_id] = status; });
        return next;
    });

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await PUT(`/meetings/${meeting._id}/attendance`, {
                records: changed.map(m => ({ user_id: m.user_id, status: draft[m.user_id] })),
            });
            showToast(res?.message || 'Attendance saved', 'success');
            onSaved();
            onClose();
        } catch (err) {
            showToast(err.message || 'Failed to save attendance.', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[998] p-4">
            <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col border border-slate-100">
                <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between gap-4">
                    <div>
                        <h3 className="text-lg font-bold text-slate-800">Attendance — {meeting.title}</h3>
                        <p className="text-sm text-slate-500 font-medium">
                            {meeting.date ? format(new Date(meeting.date), 'dd MMM yyyy') : ''}
                            {data && <> · {presentCount} present of {members.length}</>}
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100 text-slate-500"><X className="w-5 h-5" /></button>
                </div>

                {!data ? (
                    <div className="p-10 text-center text-slate-400 font-medium">Loading members...</div>
                ) : (
                    <>
                        {!canEdit && (
                            <div className="mx-6 mt-4 px-4 py-2 rounded-xl bg-amber-50 text-amber-700 text-sm font-medium">
                                Attendance can be marked manually only after the meeting has ended.
                            </div>
                        )}
                        <div className="px-6 py-3 flex flex-wrap gap-2 items-center">
                            <div className="relative flex-1 min-w-[180px]">
                                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search member..."
                                    className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                            </div>
                            {canEdit && (
                                <>
                                    <button onClick={() => markAll('attended')} className="px-3 py-2 rounded-xl text-sm font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100">Mark all present</button>
                                    <button onClick={() => markAll('absent')} className="px-3 py-2 rounded-xl text-sm font-bold text-red-700 bg-red-50 hover:bg-red-100">Mark all absent</button>
                                </>
                            )}
                        </div>
                        <div className="flex-1 overflow-y-auto custom-scrollbar px-6 pb-2">
                            {visible.length === 0 ? (
                                <div className="py-10 text-center text-slate-400 font-medium">No members found.</div>
                            ) : visible.map(m => {
                                const status = statusOf(m);
                                return (
                                    <div key={m.user_id} className="flex items-center justify-between gap-3 py-2.5 border-b border-slate-100 last:border-0">
                                        <div className="min-w-0">
                                            <div className="text-sm font-bold text-slate-700 truncate">{m.name || '—'}</div>
                                            <div className="text-xs text-slate-400 flex items-center gap-2">
                                                {m.phone}
                                                {m.locked && <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 font-bold">QR</span>}
                                                {m.source === 'manual' && <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-bold">Manual</span>}
                                            </div>
                                        </div>
                                        <div className="flex gap-1.5 shrink-0">
                                            {[['attended', 'Present', 'bg-emerald-600'], ['absent', 'Absent', 'bg-red-500']].map(([value, label, active]) => (
                                                <button key={value} onClick={() => setStatus(m, value)} disabled={!canEdit || m.locked}
                                                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors disabled:cursor-not-allowed ${status === value ? `${active} text-white` : 'bg-slate-100 text-slate-500 hover:bg-slate-200 disabled:hover:bg-slate-100'}`}>
                                                    {label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="px-6 py-4 border-t border-slate-100 flex gap-3 justify-end">
                            <button onClick={onClose} className="px-4 py-2.5 border border-slate-300 rounded-xl hover:bg-slate-50 text-slate-700 font-bold">Cancel</button>
                            {canEdit && (
                                <button onClick={handleSave} disabled={saving || changed.length === 0}
                                    className="px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-bold disabled:opacity-50 disabled:cursor-not-allowed">
                                    {saving ? 'Saving...' : `Save${changed.length ? ` (${changed.length})` : ''}`}
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

// --- Single Select Dropdown (Dashboard Style) ---
const SingleSelectDropdown = ({ options, selectedValue, onChange, placeholder, label, disabled = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleOptionClick = (value) => {
        onChange(value);
        setIsOpen(false);
    };

    const filteredOptions = options.filter(option =>
        (option.name || option.label).toLowerCase().includes(searchTerm.toLowerCase())
    );

    const selectedLabel = options.find(opt => (opt._id || opt.value) === selectedValue)?.name || options.find(opt => (opt._id || opt.value) === selectedValue)?.label;

    return (
        <div className="relative group" ref={dropdownRef}>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">{label}</label>
            <div
                className={`w-full p-2.5 bg-white border rounded-xl min-h-[46px] flex items-center justify-between transition-all duration-200 
        ${disabled ? 'opacity-60 cursor-not-allowed border-slate-200' : `cursor-pointer ${isOpen ? 'border-blue-500 ring-2 ring-blue-100 shadow-sm' : 'border-slate-200 hover:border-blue-300'}`}`}
                onClick={() => !disabled && setIsOpen(!isOpen)}
            >
                <span className={`text-sm ${selectedLabel ? 'text-slate-700 font-medium' : 'text-slate-400'}`}>
                    {selectedLabel || placeholder}
                </span>
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </div>

            {isOpen && (
                <div className="absolute top-full left-0 mt-2 w-full bg-white border border-slate-100 rounded-xl shadow-xl z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-100 origin-top">
                    <div className="p-2 border-b border-slate-50 bg-slate-50/50">
                        <input
                            type="text"
                            placeholder="Search..."
                            className="w-full p-2 text-sm bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            autoFocus
                        />
                    </div>
                    <div className="max-h-56 overflow-y-auto p-1 custom-scrollbar">
                        {filteredOptions.length === 0 && (
                            <div className="p-3 text-sm text-slate-400 text-center">No results found</div>
                        )}
                        {/* Option for clearing selection */}
                        <div
                            className="flex items-center px-3 py-2.5 rounded-lg text-sm cursor-pointer transition-colors text-slate-500 hover:bg-slate-50 hover:text-red-500"
                            onClick={() => handleOptionClick('')}
                        >
                            <span>Clear Selection</span>
                        </div>
                        {filteredOptions.map((option, idx) => {
                            const val = option._id || option.value;
                            return (
                                <div
                                    key={val || idx}
                                    className={`flex items-center px-3 py-2.5 rounded-lg text-sm cursor-pointer transition-colors ${selectedValue === val ? 'bg-blue-50 text-blue-700 font-medium' : 'text-slate-700 hover:bg-slate-50'}`}
                                    onClick={() => handleOptionClick(val)}
                                >
                                    {selectedValue === val && <Check className="w-4 h-4 mr-2 text-blue-600" />}
                                    <span className="truncate">{option.name || option.label}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

const formatDateForDisplay = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A';
const formatTimeForDisplay = (iso) => iso ? new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : 'N/A';

    const Meetings = () => {
        const [meetingPosterPreview, setMeetingPosterPreview] = useState(null);
        const [showScheduleForm, setShowScheduleForm] = useState(false);

    const [editingMeeting, setEditingMeeting] = useState(null);
    const [viewMeeting, setViewMeeting] = useState(null);
    const [attendanceMeeting, setAttendanceMeeting] = useState(null);
    const [showExportOptions, setShowExportOptions] = useState(false);
    const [selectedMeetingForExport, setSelectedMeetingForExport] = useState(null);
    const [loading, setLoading] = useState(false);

    const [meetings, setMeetings] = useState([]);
    const [cities, setCities] = useState([]);
    const [formChapters, setFormChapters] = useState([]);
    const [filterChapters, setFilterChapters] = useState([]);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const [searchTerm, setSearchTerm] = useState('');
    const master = useMasterChapter();
    const [filters, setFilters] = useState({ city_id: master.cityId, chapter_id: master.chapterId, dateRange: '', status: '' });

    // Master chapter filter (super-admin header) locks the page's city/chapter filter
    useEffect(() => {
        setFilters(f => ({ ...f, city_id: master.cityId, chapter_id: master.chapterId }));
    }, [master.cityId, master.chapterId]);
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [isPlacesReady, setIsPlacesReady] = useState(false);
    const [addressSuggestions, setAddressSuggestions] = useState([]);
    const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);

    const [scheduleData, setScheduleData] = useState({
        title: '', city_id: '', chapter_id: '', date: '', start_time: '', end_time: '',
        address: '', latitude: '', longitude: '', description: '', fees: '',
        meetingPoster: null
    });


    const [formErrors, setFormErrors] = useState({});
    const [toast, setToast] = useState({ message: '', type: '', id: null });
    const [confirmation, setConfirmation] = useState({ isOpen: false, title: '', message: '', onConfirm: () => { }, onCancel: () => { } });
    const autocompleteServiceRef = useRef(null);
    const placesServiceRef = useRef(null);
    const addressDebounceRef = useRef(null);
    const addressSessionTokenRef = useRef(null);
    const addressBlurTimeoutRef = useRef(null);

    const showToast = useCallback((message, type = 'info', duration = 3000) => {
        if (toast.id) clearTimeout(toast.id);
        const newId = setTimeout(() => setToast({ message: '', type: '', id: null }), duration);
        setToast({ message, type, id: newId });
    }, [toast.id]);

    const showConfirmation = useCallback((title, message, onConfirmCallback) => {
        setConfirmation({
            isOpen: true, title, message,
            onConfirm: () => { onConfirmCallback(); setConfirmation(prev => ({ ...prev, isOpen: false })); },
            onCancel: () => { setConfirmation(prev => ({ ...prev, isOpen: false })); }
        });
    }, []);

    useEffect(() => {
        const handler = setTimeout(() => { setDebouncedSearch(searchTerm); }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    useEffect(() => {
        const apiKey = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;
        if (!apiKey) return;

        if (window.google && window.google.maps && window.google.maps.places) {
            setIsPlacesReady(true);
            return;
        }

        const existingScript = document.querySelector('script[data-places-api]') || document.querySelector('script[data-places-api-meeting]');
        if (existingScript) {
            existingScript.addEventListener('load', () => setIsPlacesReady(true));
            return;
        }

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.setAttribute('data-places-api-meeting', 'true');
        script.onload = () => setIsPlacesReady(true);
        script.onerror = () => console.error('Failed to load Google Places API');
        document.head.appendChild(script);
    }, []);

    useEffect(() => {
        if (!isPlacesReady) return;

        if (!autocompleteServiceRef.current) {
            autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
        }

        if (!placesServiceRef.current) {
            placesServiceRef.current = new window.google.maps.places.PlacesService(document.createElement('div'));
        }

        if (!addressSessionTokenRef.current) {
            addressSessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
        }
    }, [isPlacesReady]);

    useEffect(() => {
        return () => {
            if (addressDebounceRef.current) clearTimeout(addressDebounceRef.current);
            if (addressBlurTimeoutRef.current) clearTimeout(addressBlurTimeoutRef.current);
        };
    }, []);

    useEffect(() => { setCurrentPage(1); }, [debouncedSearch, filters, itemsPerPage]);

    const fetchCities = useCallback(async () => {
        try {
            const data = await GET('/meeting/cities');
            if (data) setCities(data);
        } catch (error) { console.error('Error fetching cities:', error); }
    }, []);

    const fetchChaptersByCityId = useCallback(async (cityId) => {
        if (!cityId) return [];
        try {
            const data = await GET(`/meeting/chapters/city/${cityId}`);
            if (!data) return [];
            return data.map(chapter => ({ ...chapter, city_id: chapter.city_id?._id || chapter.city_id }));
        } catch (error) { console.error('Error fetching chapters:', error); return []; }
    }, []);

    // Logic to calculate status based on Date and Time
    const getMeetingStatus = (meeting) => {
        const now = new Date();
        const startTime = new Date(meeting.start_time);
        const endTime = new Date(meeting.end_time);

        const startDateTime = new Date(`${meeting.date.split('T')[0]}T${startTime.toISOString().split('T')[1]}`);
        const endDateTime = new Date(`${meeting.date.split('T')[0]}T${endTime.toISOString().split('T')[1]}`);

        if (isBefore(now, startDateTime)) {
            return 'Upcoming';
        } else if (isAfter(now, endDateTime)) {
            return 'Ended';
        } else {
            return 'Ongoing';
        }
    };

    const fetchMeetings = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('page', currentPage);
            params.append('limit', itemsPerPage);
            if (debouncedSearch) params.append('search', debouncedSearch);
            if (filters.city_id) params.append('cityId', filters.city_id);
            if (filters.chapter_id) params.append('chapterId', filters.chapter_id);

            let startDate, endDate;
            const now = new Date();

            if (filters.dateRange === 'last7days') {
                startDate = subDays(now, 7);
                endDate = now;
            } else if (filters.dateRange === 'lastMonth') {
                startDate = subMonths(now, 1);
                endDate = now;
            } else if (filters.dateRange === 'last3Months') {
                startDate = subMonths(now, 3);
                endDate = now;
            } else if (filters.dateRange === 'last6Months') {
                startDate = subMonths(now, 6);
                endDate = now;
            }

            if (startDate) params.append('startDate', format(startDate, 'yyyy-MM-dd'));
            if (endDate) params.append('endDate', format(endDate, 'yyyy-MM-dd'));

            const result = await GET(`/meetings?${params.toString()}`);

            let data = [];
            if (result && result.success && result.data) {
                data = result.data;
                if (result.pagination) {
                    setTotalRecords(result.pagination.total);
                    setTotalPages(result.pagination.totalPages);
                }
            } else if (Array.isArray(result)) {
                data = result;
                setTotalRecords(result.length);
                setTotalPages(1);
            } else {
                setMeetings([]); setTotalRecords(0); setTotalPages(0);
            }

            // Client-side status filter
            if (filters.status) {
                data = data.filter(meeting => getMeetingStatus(meeting) === filters.status);
            }

            // Sorting: Date Wise (Newest Top)
            data.sort((a, b) => new Date(b.date) - new Date(a.date));

            setMeetings(data);

        } catch (error) {
            console.error("Error fetching meetings:", error);
            showToast('Failed to fetch meetings.', 'error');
            setMeetings([]);
        } finally { setLoading(false); }
    }, [currentPage, itemsPerPage, debouncedSearch, filters, showToast]);

    useEffect(() => { fetchMeetings(); }, [fetchMeetings]);
    useEffect(() => { fetchCities(); }, [fetchCities]);

    useEffect(() => {
        const loadFormChapters = async () => {
            if (scheduleData.city_id) {
                const chapters = await fetchChaptersByCityId(scheduleData.city_id);
                setFormChapters(chapters);
            } else { setFormChapters([]); }
        };
        loadFormChapters();
    }, [scheduleData.city_id, fetchChaptersByCityId]);

    useEffect(() => {
        const loadFilterChapters = async () => {
            if (filters.city_id) {
                const chapters = await fetchChaptersByCityId(filters.city_id);
                setFilterChapters(chapters);
            } else { setFilterChapters([]); }
        };
        loadFilterChapters();
    }, [filters.city_id, fetchChaptersByCityId]);

    const handleScheduleInputChange = (field, value) => {
        setScheduleData({ ...scheduleData, [field]: value });
        if (formErrors[field]) setFormErrors({ ...formErrors, [field]: '' });
    };

    useEffect(() => {
        return () => {
            if (meetingPosterPreview) URL.revokeObjectURL(meetingPosterPreview);
        };
    }, [meetingPosterPreview]);

    const fetchAddressPredictions = useCallback((value) => {

        if (!isPlacesReady || !autocompleteServiceRef.current || !value?.trim()) {
            setAddressSuggestions([]);
            return;
        }

        autocompleteServiceRef.current.getPlacePredictions(
            {
                input: value,
                componentRestrictions: { country: 'in' },
                sessionToken: addressSessionTokenRef.current || undefined
            },
            (predictions, status) => {
                if (status !== window.google.maps.places.PlacesServiceStatus.OK || !predictions) {
                    setAddressSuggestions([]);
                    return;
                }
                setAddressSuggestions(predictions);
                setShowAddressSuggestions(true);
            }
        );
    }, [isPlacesReady]);

    const handleAddressChange = (value) => {
        setScheduleData((prev) => ({
            ...prev,
            address: value
        }));

        if (formErrors.address) {
            setFormErrors((prev) => ({ ...prev, address: '' }));
        }

        if (addressDebounceRef.current) clearTimeout(addressDebounceRef.current);
        addressDebounceRef.current = setTimeout(() => fetchAddressPredictions(value), 400);
    };

    const handleAddressFocus = () => {
        if (scheduleData.address) {
            fetchAddressPredictions(scheduleData.address);
        }
    };

    const handleAddressBlur = () => {
        if (addressBlurTimeoutRef.current) clearTimeout(addressBlurTimeoutRef.current);
        addressBlurTimeoutRef.current = setTimeout(() => {
            setShowAddressSuggestions(false);
        }, 150);
    };

    const handleAddressSuggestionSelect = (prediction) => {
        if (addressBlurTimeoutRef.current) clearTimeout(addressBlurTimeoutRef.current);
        setShowAddressSuggestions(false);
        setAddressSuggestions([]);

        setScheduleData((prev) => ({
            ...prev,
            address: prediction.description || prev.address
        }));

        if (!placesServiceRef.current) return;

        placesServiceRef.current.getDetails(
            {
                placeId: prediction.place_id,
                fields: ['geometry', 'formatted_address', 'name'],
                sessionToken: addressSessionTokenRef.current || undefined
            },
            (place, status) => {
                if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) return;

                const lat = place.geometry?.location?.lat();
                const lng = place.geometry?.location?.lng();

                setScheduleData((prev) => ({
                    ...prev,
                    address: place.formatted_address || place.name || prev.address,
                    latitude: typeof lat === 'number' ? lat : prev.latitude,
                    longitude: typeof lng === 'number' ? lng : prev.longitude
                }));

                addressSessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
            }
        );
    };

    const validateScheduleForm = () => {
        const errors = {};
        if (!scheduleData.title.trim()) errors.title = 'Title is required';
        if (!scheduleData.city_id) errors.city_id = 'City is required';
        if (!scheduleData.chapter_id) errors.chapter_id = 'Chapter is required';
        if (!scheduleData.date) errors.date = 'Date is required';
        if (!scheduleData.start_time) errors.start_time = 'Start time is required';
        if (!scheduleData.end_time) errors.end_time = 'End time is required';
        if (!scheduleData.address.trim()) errors.address = 'Address is required';

        if (scheduleData.date && scheduleData.start_time && scheduleData.end_time) {
            const start = new Date(`${scheduleData.date}T${scheduleData.start_time}`);
            const end = new Date(`${scheduleData.date}T${scheduleData.end_time}`);
            if (end <= start) errors.end_time = 'End time must be after start time.';
        }
        setFormErrors(errors);
            return Object.keys(errors).length === 0;
    };

    // cleanup preview object URLs
    useEffect(() => {
        return () => {
            if (meetingPosterPreview) URL.revokeObjectURL(meetingPosterPreview);
        };
    }, [meetingPosterPreview]);

    const handleScheduleMeeting = async () => {
        if (validateScheduleForm()) {
            showConfirmation(editingMeeting ? 'Update Meeting' : 'Schedule Meeting', 'Are you sure?', async () => {
                try {
                    const formData = new FormData();

                    formData.append('title', scheduleData.title);
                    formData.append('city_id', scheduleData.city_id);
                    formData.append('chapter_id', scheduleData.chapter_id);
                    formData.append('date', new Date(scheduleData.date).toISOString());
                    formData.append('start_time', new Date(`${scheduleData.date}T${scheduleData.start_time}`).toISOString());
                    formData.append('end_time', new Date(`${scheduleData.date}T${scheduleData.end_time}`).toISOString());
                    formData.append('address', scheduleData.address);
                    formData.append('latitude', String(parseFloat(scheduleData.latitude) || 0));
                    formData.append('longitude', String(parseFloat(scheduleData.longitude) || 0));
                    if (scheduleData.description) formData.append('description', scheduleData.description);
                    formData.append('fees', String(parseFloat(scheduleData.fees) || 0));

                    // Optional meeting poster (images only)
                    if (scheduleData.meetingPoster) {
                        formData.append('meeting_poster', scheduleData.meetingPoster);
                    }

                    if (editingMeeting) {
                        await fetch(`${process.env.REACT_APP_API_BASE_URL}/meetings/${editingMeeting._id}`, {
                            method: 'PUT',
                            headers: {
                                'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                            },
                            body: formData,
                        });
                    } else {
                        await fetch(`${process.env.REACT_APP_API_BASE_URL}/meetings/`, {
                            method: 'POST',
                            headers: {
                                'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                            },
                            body: formData,
                        });
                    }

                    showToast(editingMeeting ? 'Meeting updated!' : 'Meeting scheduled!', 'success');
                    setShowScheduleForm(false);
                    setEditingMeeting(null);
                    setScheduleData({ title: '', city_id: '', chapter_id: '', date: '', start_time: '', end_time: '', address: '', latitude: '', longitude: '', description: '', fees: '', meetingPoster: null });
                    fetchMeetings();
                } catch (error) { showToast(`Failed: ${error.message}`, 'error'); }
            });
        }
    };

    const handleDeleteMeeting = (meetingId) => {
        showConfirmation('Delete Meeting', 'Are you sure?', async () => {
            try {
                await DELETE(`/meetings/${meetingId}`);
                showToast('Meeting deleted!', 'success');
                fetchMeetings();
            } catch (error) { showToast(`Failed to delete: ${error.message}`, 'error'); }
        });
    };

    const handleEditMeeting = (meeting) => {
        setEditingMeeting(meeting);
        setScheduleData({
            title: meeting.title,
            city_id: meeting.city_id?._id || '',
            chapter_id: meeting.chapter_id?._id || '',
            date: meeting.date ? new Date(meeting.date).toISOString().split('T')[0] : '',
            start_time: meeting.start_time ? new Date(meeting.start_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }) : '',
            end_time: meeting.end_time ? new Date(meeting.end_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }) : '',
            address: meeting.address,
            latitude: meeting.latitude || '',
            longitude: meeting.longitude || '',
            description: meeting.description || '',
            fees: meeting.fees || '',
            meetingPoster: null
        });
        setShowScheduleForm(true);
    };

    const handleExport = (meeting) => {
        setSelectedMeetingForExport(meeting);
        setShowExportOptions(true);
    };

    const downloadQRCode = () => {
        if (!selectedMeetingForExport?.qrCodeDataUrl) { showToast('No QR Code available', 'error'); return; }
        const link = document.createElement('a');
        link.href = selectedMeetingForExport.qrCodeDataUrl;
        link.download = `qrcode_${selectedMeetingForExport._id}.png`;
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
        showToast('QR Code downloaded!', 'success'); setShowExportOptions(false);
    };

    const downloadExcel = async () => {
        if (!selectedMeetingForExport) return;
        try {
            const token = localStorage.getItem("authToken");
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/meetings/${selectedMeetingForExport._id}/export-attendance`, {
                headers: { "Content-Type": "application/json", "Authorization": token ? `Bearer ${token}` : "" }
            });
            if (!response.ok) throw new Error('Download Failed');
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = `attendance_${selectedMeetingForExport._id}.csv`;
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            showToast('CSV downloaded!', 'success'); setShowExportOptions(false);
        } catch (error) { showToast(`Download Failed: ${error.message}`, 'error'); }
    };

    const handleCreateClick = () => {
        setEditingMeeting(null);
        setShowScheduleForm(true);
        setMeetingPosterPreview(null);
        setScheduleData({ title: '', city_id: master.cityId, chapter_id: master.chapterId, date: '', start_time: '', end_time: '', address: '', latitude: '', longitude: '', description: '', fees: '', meetingPoster: null });
        setFormErrors({});
        setAddressSuggestions([]);
        setShowAddressSuggestions(false);
    };

    const resetFilters = () => {
        setFilters({ city_id: master.cityId, chapter_id: master.chapterId, dateRange: '', status: '' });
        setSearchTerm('');
    };

    return (
        <div>
            <CustomStyles />

            <div className="space-y-5">

                {/* --- Filter Section --- */}
                <div className="relative z-20 bg-white rounded-2xl shadow-sm border border-slate-200 p-5 animate-fade-in-up">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4 pb-3 border-b border-slate-100">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-50 rounded-xl text-blue-600"><Filter className="w-5 h-5" /></div>
                            <h2 className="text-base font-bold text-slate-800 uppercase tracking-wide">Filter Meetings</h2>
                        </div>
                        <button onClick={handleCreateClick} className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl text-sm font-bold shadow-lg shadow-blue-200 hover:shadow-blue-300 transition-all flex items-center gap-2">
                            <Plus className="w-5 h-5" /> <span>Schedule</span>
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-5 gap-5 items-end">
                        <div className="relative">
                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">Search</label>
                            <div className="relative">
                                <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search..." className="w-full p-2.5 pl-10 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-700" />
                                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3.5" />
                            </div>
                        </div>

                        {/* Updated Dropdowns */}
                        <SingleSelectDropdown
                            label="City"
                            placeholder="All Cities"
                            options={cities}
                            selectedValue={filters.city_id}
                            onChange={(val) => setFilters({ ...filters, city_id: val, chapter_id: '' })}
                            disabled={!!master.chapterId}
                        />

                        <SingleSelectDropdown
                            label="Chapter"
                            placeholder="All Chapters"
                            options={filterChapters}
                            selectedValue={filters.chapter_id}
                            onChange={(val) => setFilters({ ...filters, chapter_id: val })}
                            disabled={!!master.chapterId}
                        />

                        {/* Date Filter Dropdown */}
                        <SingleSelectDropdown
                            label="Date Filter"
                            placeholder="All Time"
                            options={[
                                { label: 'All Time', value: '' },
                                { label: 'Last 7 Days', value: 'last7days' },
                                { label: 'Last Month', value: 'lastMonth' },
                                { label: 'Last 3 Months', value: 'last3Months' },
                                { label: 'Last 6 Months', value: 'last6Months' },
                            ]}
                            selectedValue={filters.dateRange}
                            onChange={(val) => setFilters({ ...filters, dateRange: val })}
                        />

                        <div className="flex gap-2">
                            <div className="flex-1">
                                <SingleSelectDropdown
                                    label="Status"
                                    placeholder="All Status"
                                    options={[
                                        { label: 'All Status', value: '' },
                                        { label: 'Upcoming', value: 'Upcoming' },
                                        { label: 'Ongoing', value: 'Ongoing' },
                                        { label: 'Ended', value: 'Ended' },
                                    ]}
                                    selectedValue={filters.status}
                                    onChange={(val) => setFilters({ ...filters, status: val })}
                                />
                            </div>
                            <button onClick={resetFilters} className="px-3 py-2.5 mt-6 bg-white border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 hover:text-blue-600 hover:border-blue-200 transition-all shadow-sm h-[46px] flex-shrink-0" title="Reset Filters"><RefreshCw className="w-5 h-5" /></button>
                        </div>
                    </div>
                    {master.chapterId && <div className="mt-3"><MasterChapterLockNote /></div>}
                </div>

                {/* Table */}
                <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                    <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-white">
                        <h2 className="text-lg font-bold text-slate-800">Meetings List</h2>
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-slate-500">Rows:</span>
                            <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="p-1.5 border border-slate-200 rounded-lg text-sm font-bold text-slate-700 bg-white outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer">
                                <option value={10}>10</option><option value={25}>25</option><option value={50}>50</option>
                            </select>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-100">
                            <thead className="bg-slate-50/50">
                                <tr>
                                    {['SR.', 'Title', 'Date', 'City', 'Chapter', 'Time', 'Status', 'Attendees', 'Actions'].map(h => <th key={h} className="px-5 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">{h}</th>)}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-slate-100">
                                {loading ? (
                                    <tr><td colSpan="9" className="px-6 py-10 text-center text-slate-400 font-medium">Loading meetings...</td></tr>
                                ) : meetings.length > 0 ? (
                                    meetings.map((meeting, index) => {
                                        const status = getMeetingStatus(meeting);
                                        let statusClass = '';
                                        // UPDATED STATUS COLORS
                                        if (status === 'Upcoming') statusClass = 'bg-amber-50 text-amber-600 border-amber-200'; // Yellow/Amber
                                        else if (status === 'Ongoing') statusClass = 'bg-emerald-50 text-emerald-600 border-emerald-200 animate-pulse'; // Green
                                        else statusClass = 'bg-red-50 text-red-600 border-red-200'; // Red (Ended)

                                        return (
                                            <tr key={meeting._id} className="hover:bg-slate-50/80 transition-colors group">
                                                <td className="px-5 py-3 whitespace-nowrap text-sm font-bold text-slate-500">{((currentPage - 1) * itemsPerPage) + index + 1}</td>
                                                <td className="px-6 py-4 font-bold text-slate-800 text-sm">{meeting.title}</td>
                                                <td className="px-5 py-3 whitespace-nowrap text-sm font-medium text-slate-600"><div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-slate-400" /> {formatDateForDisplay(meeting.date)}</div></td>
                                                <td className="px-5 py-3 whitespace-nowrap text-sm font-medium text-slate-600"><span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-50 text-blue-600 border border-blue-100">{meeting.city_id?.name || 'N/A'}</span></td>
                                                <td className="px-5 py-3 whitespace-nowrap text-sm font-medium text-slate-600">{meeting.chapter_id?.name || 'N/A'}</td>
                                                <td className="px-5 py-3 whitespace-nowrap text-sm font-medium text-slate-600"><div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-slate-400" /> {formatTimeForDisplay(meeting.start_time)} - {formatTimeForDisplay(meeting.end_time)}</div></td>
                                                <td className="px-5 py-3 whitespace-nowrap text-sm font-medium">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${statusClass}`}>
                                                        {status}
                                                    </span>
                                                </td>
                                                <td className="px-5 py-3 text-sm font-bold text-slate-700">{meeting.totalAttendances || 0}</td>
                                                <td className="px-5 py-3 whitespace-nowrap text-sm font-medium">
                                                    {/* UPDATED: Direct Action Buttons */}
                                                    <div className="flex gap-2">
                                                        <button onClick={() => setViewMeeting(meeting)} className="p-2 rounded-lg text-blue-600 bg-blue-50 hover:bg-blue-100 transition-colors" title="View Details">
                                                            <Eye className="w-4 h-4" />
                                                        </button>
                                                        <button onClick={() => setAttendanceMeeting(meeting)} className="p-2 rounded-lg text-emerald-600 bg-emerald-50 hover:bg-emerald-100 transition-colors" title="Attendance">
                                                            <ClipboardCheck className="w-4 h-4" />
                                                        </button>
                                                        <button onClick={() => handleEditMeeting(meeting)} className="p-2 rounded-lg text-amber-600 bg-amber-50 hover:bg-amber-100 transition-colors" title="Edit Meeting">
                                                            <Pencil className="w-4 h-4" />
                                                        </button>
                                                        <button onClick={() => handleExport(meeting)} className="p-2 rounded-lg text-purple-600 bg-purple-50 hover:bg-purple-100 transition-colors" title="Export">
                                                            <Download className="w-4 h-4" />
                                                        </button>
                                                        <button onClick={() => handleDeleteMeeting(meeting._id)} className="p-2 rounded-lg text-red-600 bg-red-50 hover:bg-red-100 transition-colors" title="Delete">
                                                            <Trash className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr><td colSpan="9" className="px-6 py-10 text-center text-slate-400 font-medium">No meetings found.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="px-6 py-4 bg-white border-t border-slate-100 flex items-center justify-between">
                        <div className="text-sm font-medium text-slate-500">
                            Showing <span className="font-bold text-slate-700">{meetings.length > 0 ? ((currentPage - 1) * itemsPerPage) + 1 : 0}</span> to <span className="font-bold text-slate-700">{Math.min(currentPage * itemsPerPage, totalRecords)}</span> of <span className="font-bold text-slate-700">{totalRecords}</span> entries
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1 || loading} className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">Previous</button>
                            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || loading} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm">Next</button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modals */}
            {showScheduleForm && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[999] p-4 animate-in fade-in">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-in zoom-in-95 custom-scrollbar">
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-white sticky top-0 z-10">
                            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                <div className="p-2 bg-blue-50 rounded-xl text-blue-600"><Calendar className="w-5 h-5" /></div>
                                {editingMeeting ? 'Edit Meeting' : 'Schedule Meeting'}
                            </h3>
                            <button onClick={() => setShowScheduleForm(false)} className="p-2 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="p-6 space-y-5">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div><label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Title *</label><input type="text" value={scheduleData.title} onChange={e => handleScheduleInputChange('title', e.target.value)} className={`w-full p-3 bg-slate-50 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-800 ${formErrors.title ? 'border-red-500' : 'border-slate-200'}`} placeholder="Title" /></div>
                                <div><label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Fees</label><input type="text" value={scheduleData.fees} onChange={e => handleScheduleInputChange('fees', e.target.value.replace(/[^0-9]/g, ''))} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-800" placeholder="0" /></div>

                                <div><label className="text-xs font-bold text-slate-500 uppercase tracking-wide">City *</label><select value={scheduleData.city_id} onChange={e => handleScheduleInputChange('city_id', e.target.value)} className={`w-full p-3 bg-slate-50 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-700 ${formErrors.city_id ? 'border-red-500' : 'border-slate-200'}`}><option value="">Select City</option>{cities.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}</select></div>
                                <div><label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Chapter *</label><select value={scheduleData.chapter_id} onChange={e => handleScheduleInputChange('chapter_id', e.target.value)} disabled={!scheduleData.city_id} className={`w-full p-3 bg-slate-50 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-700 disabled:opacity-50 ${formErrors.chapter_id ? 'border-red-500' : 'border-slate-200'}`}><option value="">Select Chapter</option>{formChapters.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}</select></div>
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                                <div><label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Date *</label><input type="date" value={scheduleData.date} onChange={e => handleScheduleInputChange('date', e.target.value)} className={`w-full p-3 bg-slate-50 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-700 ${formErrors.date ? 'border-red-500' : 'border-slate-200'}`} /></div>
                                <div><label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Start *</label><input type="time" value={scheduleData.start_time} onChange={e => handleScheduleInputChange('start_time', e.target.value)} className={`w-full p-3 bg-slate-50 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-700 ${formErrors.start_time ? 'border-red-500' : 'border-slate-200'}`} /></div>
                                <div><label className="text-xs font-bold text-slate-500 uppercase tracking-wide">End *</label><input type="time" value={scheduleData.end_time} onChange={e => handleScheduleInputChange('end_time', e.target.value)} className={`w-full p-3 bg-slate-50 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-700 ${formErrors.end_time ? 'border-red-500' : 'border-slate-200'}`} /></div>
                            </div>

                            {/* Meeting Poster (Optional) */}
                            <div>
                                <div className="flex items-center justify-between gap-3">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Meeting Poster (Optional)</label>
                                    {editingMeeting ? (
                                        <span className="text-[11px] font-bold text-slate-400">Replace if needed</span>
                                    ) : (
                                        <span className="text-[11px] font-bold text-slate-400">Upload image</span>
                                    )}
                                </div>

                                <div className="mt-2 flex items-start gap-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-20 h-20 rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-sm flex items-center justify-center">
                                            {meetingPosterPreview ? (
                                                <img src={meetingPosterPreview} alt="Meeting Poster Preview" className="w-full h-full object-cover" />
                                            ) : (editingMeeting?.meetingPoster ? (
                                                <img src={editingMeeting.meetingPoster} alt="Meeting Poster" className="w-full h-full object-cover" />
                                            ) : (
                                                <span className="text-xs font-bold text-slate-400">Preview</span>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="flex-1">
                                        <input
                                            type="file"
                                            accept="image/*"
name="meeting_poster"
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (!file) {
                                                    setScheduleData(prev => ({ ...prev, meetingPoster: null }));
                                                    setMeetingPosterPreview(editingMeeting?.meetingPoster || null);
                                                    return;
                                                }


                                                if (!isImageFile(file)) {
                                                    showToast('Please select an image file.', 'error');
                                                    e.target.value = '';
                                                    return;
                                                }

                                                setScheduleData(prev => ({ ...prev, meetingPoster: file }));
                                                const previewUrl = URL.createObjectURL(file);
                                                setMeetingPosterPreview(previewUrl);
                                            }}
                                            className="w-full p-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-700"
                                        />
                                        <p className="text-[11px] text-slate-500 mt-1">Only image files are allowed (optional).</p>
                                    </div>
                                </div>
                            </div>

                            <div className="col-span-2 relative">
                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Address *</label>
                                <input
                                    type="text"
                                    value={scheduleData.address}
                                    onChange={e => handleAddressChange(e.target.value)}
                                    onFocus={handleAddressFocus}
                                    onBlur={handleAddressBlur}
                                    className={`w-full p-3 bg-slate-50 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-800 ${formErrors.address ? 'border-red-500' : 'border-slate-200'}`}
                                    placeholder="Start typing address..."
                                    autoComplete="off"
                                />
                                {showAddressSuggestions && addressSuggestions.length > 0 && (
                                    <ul className="absolute z-[1000] mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-56 overflow-y-auto custom-scrollbar">
                                        {addressSuggestions.map((prediction) => (
                                            <li
                                                key={prediction.place_id}
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    handleAddressSuggestionSelect(prediction);
                                                }}
                                                className="px-3 py-2 cursor-pointer hover:bg-slate-100 text-sm text-slate-700"
                                            >
                                                <div className="font-semibold text-slate-700">
                                                    {prediction.structured_formatting?.main_text || prediction.description}
                                                </div>
                                                {prediction.structured_formatting?.secondary_text && (
                                                    <div className="text-xs text-slate-500">
                                                        {prediction.structured_formatting.secondary_text}
                                                    </div>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                <input type="hidden" value={scheduleData.latitude} readOnly />
                                <input type="hidden" value={scheduleData.longitude} readOnly />
                            </div>
                            <div><label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Description</label><textarea value={scheduleData.description} onChange={e => handleScheduleInputChange('description', e.target.value)} rows="2" className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-800" placeholder="Details..." /></div>
                        </div>
                        <div className="p-6 border-t border-slate-100 flex gap-3 bg-slate-50/50 sticky bottom-0 z-10">
                            <button onClick={() => setShowScheduleForm(false)} className="flex-1 px-4 py-3 border border-slate-200 rounded-xl font-bold text-slate-600 hover:bg-slate-100 transition-colors">Cancel</button>
                            <button onClick={handleScheduleMeeting} className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 shadow-lg shadow-blue-200 transition-colors">Save</button>
                        </div>
                    </div>
                </div>
            )}

 {/* --- View User Modal (Updated: Clean Professional Light Theme) --- */}
            {viewMeeting && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[999] p-4 animate-in fade-in duration-200">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200 border border-slate-100">
                        
                        {/* --- Header --- */}
                        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-white sticky top-0 z-10">
                            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                <div className="p-2 bg-slate-100 text-slate-600 rounded-xl">
                                    <Eye className="w-5 h-5" />
                                </div>
                                Meeting Details
                            </h3>
                            <button 
                                onClick={() => setViewMeeting(null)} 
                                className="p-2 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* --- Body (Side by Side Grid) --- */}
                        <div className="p-6 md:p-8 bg-slate-50/30">
                            <div className="flex flex-col md:flex-row gap-5">
                                
                                {/* --- Left Side: Meeting Information --- */}
                                <div className="flex-1 space-y-6">
                                    {/* Title & Price Header */}
                                    <div className="flex flex-wrap justify-between items-start gap-4 border-b border-slate-100 pb-6">
                                        <div>
                                            <h4 className="text-2xl font-extrabold text-slate-800 leading-tight mb-1">
                                                {viewMeeting.title}
                                            </h4>
                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-xs font-bold bg-slate-100 text-slate-500 border border-slate-200">
                                                {viewMeeting.chapter_id?.name || 'General Chapter'}
                                            </span>
                                        </div>
                                        <div className="flex-shrink-0">
                                            {viewMeeting.fees > 0 ? (
                                                <span className="inline-flex items-center px-4 py-2 rounded-xl text-sm font-bold bg-white text-emerald-700 border border-emerald-200 shadow-sm">
                                                    ₹{viewMeeting.fees}
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center px-4 py-2 rounded-xl text-sm font-bold bg-white text-blue-600 border border-blue-200 shadow-sm">
                                                    Free
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Meeting Poster */}
                                    {viewMeeting.meetingPoster ? (
                                        <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-sm bg-white">
                                            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                                                <span className="inline-flex items-center justify-center p-2 bg-slate-50 text-slate-600 rounded-xl">
                                                    <span className="text-xs font-bold">Poster</span>
                                                </span>
                                                <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Meeting Poster</p>
                                            </div>
                                            <div className="w-full h-[220px] bg-slate-50">
                                                <img
                                                    src={viewMeeting.meetingPoster}
                                                    alt="Meeting Poster"
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                        </div>
                                    ) : null}


                                    {/* Info Grid */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        {/* Date & Time */}
                                        <div className="p-4 bg-white rounded-2xl border border-slate-200 shadow-sm flex items-start gap-3">
                                            <Calendar className="w-5 h-5 text-blue-500 mt-0.5" />
                                            <div>
                                                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-1">Date</p>
                                                <p className="text-sm font-bold text-slate-700">{formatDateForDisplay(viewMeeting.date)}</p>
                                            </div>
                                        </div>

                                        <div className="p-4 bg-white rounded-2xl border border-slate-200 shadow-sm flex items-start gap-3">
                                            <Clock className="w-5 h-5 text-amber-500 mt-0.5" />
                                            <div>
                                                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-1">Time</p>
                                                <p className="text-sm font-bold text-slate-700">
                                                    {formatTimeForDisplay(viewMeeting.start_time)} - {formatTimeForDisplay(viewMeeting.end_time)}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Location - Full Width */}
                                        <div className="col-span-1 sm:col-span-2 p-4 bg-white rounded-2xl border border-slate-200 shadow-sm flex items-start gap-3">
                                            <MapPin className="w-5 h-5 text-red-500 mt-0.5" />
                                            <div>
                                                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-1">Venue</p>
                                                <p className="text-sm font-bold text-slate-700 leading-snug">{viewMeeting.address}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Description */}
                                    {viewMeeting.description && (
                                        <div>
                                            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-2 ml-1">Description</p>
                                            <div className="p-4 bg-white rounded-2xl border border-slate-200 shadow-sm text-sm text-slate-600 leading-relaxed">
                                                {viewMeeting.description}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* --- Right Side: QR Code Card (Clean Professional Style) --- */}
                                {viewMeeting.qrCodeDataUrl && (
                                    <div className="md:w-[300px] flex-shrink-0">
                                        <div className="h-full bg-white border border-slate-200 rounded-3xl p-6 text-center flex flex-col items-center justify-center shadow-sm">
                                            
                                            <div className="mb-6 flex flex-col items-center gap-2">
                                                <div className="p-2 bg-slate-50 rounded-full text-slate-400">
                                                    <QrCode className="w-5 h-5" />
                                                </div>
                                                <h5 className="text-sm font-bold text-slate-700 uppercase tracking-wide">
                                                    Scan Attendance
                                                </h5>
                                            </div>
                                            
                                            <div className="p-2 bg-white border-2 border-slate-100 rounded-2xl mb-6">
                                                <img 
                                                    src={viewMeeting.qrCodeDataUrl} 
                                                    alt="Meeting QR Code" 
                                                    className="w-48 h-48 object-contain" 
                                                />
                                            </div>
                                            
                                            <div className="bg-slate-50 px-4 py-3 rounded-xl w-full">
                                                <p className="text-xs text-slate-500 leading-relaxed font-medium">
                                                    Please show this QR code at the venue entrance.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* --- Footer --- */}
                        <div className="px-6 py-4 border-t border-slate-100 bg-white flex justify-end sticky bottom-0 z-10">
                            <button 
                                onClick={() => setViewMeeting(null)} 
                                className="px-8 py-2.5 bg-slate-800 text-white font-bold rounded-xl hover:bg-slate-900 transition-all shadow-lg shadow-slate-200 active:scale-[0.98]"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showExportOptions && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[999] p-4 animate-in fade-in">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-sm w-full p-6 text-center border border-slate-100 scale-100 animate-in zoom-in-95">
                        <h3 className="text-lg font-bold text-slate-800 mb-6">Export Options</h3>
                        <div className="space-y-3">
                            <button onClick={downloadQRCode} className="w-full py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 flex items-center justify-center gap-2 shadow-lg shadow-blue-200 transition-colors">
                                <QrCode className="w-5 h-5" /> Download QR Code
                            </button>
                            <button onClick={downloadExcel} className="w-full py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 flex items-center justify-center gap-2 shadow-lg shadow-emerald-200 transition-colors">
                                <FileSpreadsheet className="w-5 h-5" /> Download Excel
                            </button>
                        </div>
                        <button onClick={() => setShowExportOptions(false)} className="mt-6 text-slate-400 font-medium hover:text-slate-600 transition-colors">Close</button>
                    </div>
                </div>
            )}

            {attendanceMeeting && (
                <AttendanceModal meeting={attendanceMeeting} onClose={() => setAttendanceMeeting(null)} onSaved={fetchMeetings} showToast={showToast} />
            )}

            <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: '', type: '', id: null })} />
            <ConfirmationModal isOpen={confirmation.isOpen} title={confirmation.title} message={confirmation.message} onConfirm={confirmation.onConfirm} onCancel={confirmation.onCancel} />
        </div>
    );
};

export default Meetings;